import React, { Fragment, useState, useRef, useEffect } from 'react';
import CurrentPlan from '../../common/components/CurrentPlan';
import {
    Box,
    IconButton,
    TextField,
    Typography,
    InputAdornment,
    Paper,
    MenuItem,
    Select,
    FormControl,
    Grid,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Delete as DeleteIcon,
    Add,
    WatchLater,
    ArrowRight,
    KeyboardArrowDown,
    MoreVert,
    Close as CloseIcon,
    Description as DescriptionIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    ContentCopy as ContentCopyIcon,
    Check as CheckIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import CommonButton from '../../common/components/CommonButton';
import CommonCard from '../../common/components/CommonCard';
import theme from '../../theme';
import { ChatBotOption, ServiceOffer, CHATBOX_STRINGS, UserTypeId } from '../../common/const';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../features/authSlice';
import { deleteSession, getMessages, getSessionList, sendMessage, startSession, updateSession } from '../../api/aichatApi';
import moment from 'moment';
import { getAllAssignSubject, getAllSubject } from '../../api/subjectApi';

function ChatBox() {
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [chats, setChats] = useState([]);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [selectedArea, setSelectedArea] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [messageInput, setMessageInput] = useState('');
    const [showInitialOptions, setShowInitialOptions] = useState(true);
    const [currentChat, setCurrentChat] = useState(null);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [moreDialogOpen, setMoreDialogOpen] = useState(false);
    const [editableTitle, setEditableTitle] = useState('');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [subjects, setSubject] = useState([]);

    // Streaming, copy, edit state
    const [streamingMessageId, setStreamingMessageId] = useState(null);
    const [streamingText, setStreamingText] = useState('');
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const streamIntervalRef = useRef(null);

    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [currentChat?.messages, streamingText]);

    // Cleanup stream interval on unmount
    useEffect(() => {
        return () => {
            if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
        };
    }, []);

    const handleSelectChat = (chatId) => {
        setSelectedChatId(chatId);
        setShowInitialOptions(false);
        setAttachedFiles([]);
        setEditingMessageId(null);
    };

    const handleNewChat = () => {
        setSelectedChatId(null);
        setCurrentChat(null);
        setShowInitialOptions(true);
        setMessageInput('');
        setAttachedFiles([]);
        setEditingMessageId(null);
        setStreamingMessageId(null);
        setStreamingText('');
    };

    const handleSelectOption = async (optionValue) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await startSession({
                title: CHATBOX_STRINGS.SESSION_DEFAULT_TITLE,
                placeholder: optionValue,
                service: ChatBotOption.find(opt => opt.label === optionValue)?.value,
                student_id: user?.student_id,
            });
            if (response?.data) {
                dispatch(setLoading({ loading: false }));
                setSelectedChatId(response?.data?.chat_session_id);
                setShowInitialOptions(false);
                getChatHistory();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleChangeOption = async (newOption) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await updateSession(selectedChatId, {
                title: CHATBOX_STRINGS.SESSION_DEFAULT_TITLE,
                placeholder: newOption,
                service: ChatBotOption.find(opt => opt.label === newOption)?.value,
                student_id: user?.student_id,
            });
            if (response?.data) {
                dispatch(setLoading({ loading: false }));
                setSelectedChatId(response?.data?.chat_session_id);
                getChats();
                getChatHistory();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleChangeSubject = async (newOption) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await updateSession(selectedChatId, {
                title: currentChat?.title,
                subject: subjects?.find((a) => a.value === newOption)?.label,
                subject_id: newOption,
                student_subject_id: subjects?.find((a) => a.value === newOption)?.student_subject_id,
            });
            if (response?.data) {
                dispatch(setLoading({ loading: false }));
                setSelectedChatId(response?.data?.chat_session_id);
                getChats();
                getChatHistory();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setAttachedFiles(prev => [...prev, ...files]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Copy handler — works for both AI and student messages
    const handleCopyMessage = (text, id) => {
        navigator.clipboard?.writeText(text).catch(() => { });
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Edit handlers (student messages only)
    const handleStartEdit = (message) => {
        setEditingMessageId(message.id);
        setEditingText(message.message);
    };

    const handleCancelEdit = () => {
        setEditingMessageId(null);
        setEditingText('');
    };

    // Build the downloadable file URL
    const buildFileUrl = (fileUrl) => {
        // If the URL is already absolute (starts with http/https), use it directly
        if (fileUrl?.startsWith('http://') || fileUrl?.startsWith('https://')) {
            return fileUrl;
        }
        // Otherwise build using base URL + query params pattern (same as student attachments)
        return `${process.env.REACT_APP_BASE_URL}${CHATBOX_STRINGS.FILE_DOWNLOAD_QUERY}${fileUrl}${CHATBOX_STRINGS.FILE_DOWNLOAD_PARAM}`;
    };

    // Handle file download/open
    const handleOpenAttachment = (file) => {
        const url = buildFileUrl(file.url);
        window.open(url, '_blank');
    };

    // Get icon color based on file type
    const getFileIconColor = (filename = '') => {
        const ext = filename.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return '#E53935';
            case 'doc':
            case 'docx': return '#1565C0';
            case 'xls':
            case 'xlsx': return '#2E7D32';
            case 'ppt':
            case 'pptx': return '#E65100';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp': return '#6A1B9A';
            default: return '#455A64';
        }
    };

    // Send message with streaming AI response
    const handleSendMessage = async (messageId, editingText) => {
        if ((!messageInput.trim() && !editingText?.trim()) && attachedFiles.length === 0) return;
        if (!selectedChatId) {
            showNotification(CHATBOX_STRINGS.SELECT_OPTION_ERROR, 'error');
            return;
        }

        const formData = new FormData();
        formData.append('chat_session_id', selectedChatId);
        formData.append('message', messageInput || editingText);
        console.log(messageId);
        if (messageId) {
            formData.append('edit_message_id', messageId);
        }
        if (attachedFiles.length > 0) {
            attachedFiles.forEach((a) => formData.append('attachments', a));
        }

        const tempUserId = 'temp-user-' + Date.now();
        const tempAiId = 'temp-ai-' + Date.now();

        const tempUserMsg = {
            id: tempUserId,
            role: 'STUDENT',
            message: messageInput || editingText,
            created_at: new Date().toISOString(),
            attachment_urls: attachedFiles.map(f => ({ filename: f.name, url: '' })),
        };
        const tempAiMsg = {
            id: tempAiId,
            role: 'AI',
            message: '',
            created_at: new Date().toISOString(),
            attachment_urls: [],
        };

        setCurrentChat(prev => ({
            ...prev,
            messages: [...(prev?.messages || []), tempUserMsg, tempAiMsg],
        }));
        setStreamingMessageId(tempAiId);
        setStreamingText('');
        setMessageInput('');
        setAttachedFiles([]);
        setEditingMessageId(null);
        setEditingText('');

        try {
            const response = await sendMessage(formData);
            if (response?.success) {
                const aiResponse = response?.data?.ai_message || response?.data?.reply || response?.data?.message || '';

                let i = 0;
                if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);

                streamIntervalRef.current = setInterval(() => {
                    if (i < aiResponse.length) {
                        setStreamingText(aiResponse.slice(0, ++i));
                    } else {
                        clearInterval(streamIntervalRef.current);
                        streamIntervalRef.current = null;
                        setStreamingMessageId(null);
                        setStreamingText('');
                        getChats();
                        getChatHistory();
                    }
                }, 15);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            setStreamingMessageId(null);
            setStreamingText('');
            setCurrentChat(prev => ({
                ...prev,
                messages: prev?.messages?.filter(m => m.id !== tempUserId && m.id !== tempAiId),
            }));
        }
    };

    const handleDeleteChat = async (chatId, e) => {
        try {
            e.stopPropagation();
            dispatch(setLoading({ loading: true }));
            const response = await deleteSession(chatId);
            if (response?.success) {
                showNotification(response?.message, 'success');
                getChatHistory();
                if (chatId === selectedChatId) {
                    setCurrentChat(null);
                    setSelectedChatId(null);
                }
                dispatch(setLoading({ loading: false }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const handleUpdateTitle = async () => {
        if (!editableTitle.trim()) return;
        try {
            dispatch(setLoading({ loading: true }));
            const response = await updateSession(selectedChatId, {
                title: editableTitle,
                placeholder: currentChat?.placeholder,
                service: currentChat?.service,
                student_id: user?.student_id,
            });
            if (response?.data) {
                setCurrentChat(prev => ({ ...prev, title: editableTitle }));
                setIsEditingTitle(false);
                getChatHistory();
                showNotification(CHATBOX_STRINGS.TITLE_UPDATED_SUCCESS, 'success');
            }
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const getSelectedOptionDetails = () => {
        return ChatBotOption.find(opt => opt.value === currentChat?.service);
    };

    const getInputPlaceholder = () => {
        switch (currentChat?.service) {
            case CHATBOX_STRINGS.SAT_ACT_COACH: return CHATBOX_STRINGS.PLACEHOLDER_SAT_ACT;
            case CHATBOX_STRINGS.AI_TUTOR: return CHATBOX_STRINGS.PLACEHOLDER_AI_TUTOR;
            case CHATBOX_STRINGS.AI_PROJECT_MENTOR: return CHATBOX_STRINGS.PLACEHOLDER_AI_PROJECT_MENTOR;
            case CHATBOX_STRINGS.AI_COUNSELOR: return CHATBOX_STRINGS.PLACEHOLDER_AI_COUNSELOR;
            case CHATBOX_STRINGS.SKILLS_TECH: return CHATBOX_STRINGS.PLACEHOLDER_SKILLS_TECH;
            default: return CHATBOX_STRINGS.PLACEHOLDER_DEFAULT;
        }
    };

    const getChats = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getMessages(selectedChatId);
            setCurrentChat(response?.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (selectedChatId) {
            getChats();
        }
    }, [selectedChatId]);

    const getChatHistory = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getSessionList(selectedArea);
            setChats(response?.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        getChatHistory();
    }, [selectedArea]);

    const handleOpenMoreDialog = () => {
        setEditableTitle(currentChat?.title || '');
        setIsEditingTitle(false);
        setMoreDialogOpen(true);
    };

    const getSubjects = async (data) => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getAllSubject(1, 100, ServiceOffer.academic, user?.student_current_grade);
            setSubject([...data, ...response?.data?.data?.map((a) => ({
                label: a?.name,
                value: a?.subject_id,
            }))]);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    const getAssignedSubject = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getAllAssignSubject(
                currentChat?.service === CHATBOX_STRINGS.AI_TUTOR ? ServiceOffer.tutor : ServiceOffer.skillCoach,
                user?.student_id
            );
            if (currentChat?.service === CHATBOX_STRINGS.AI_TUTOR) {
                getSubjects(response?.data?.map((a) => ({
                    value: a?.subject_id,
                    student_subject_id: a?.student_subject_id,
                    label: a?.subject?.name,
                })));
            } else {
                setSubject(response?.data?.map((a) => ({
                    value: a?.subject_id,
                    student_subject_id: a?.student_subject_id,
                    label: a?.subject?.name,
                })));
            }
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (currentChat?.service === CHATBOX_STRINGS.AI_TUTOR || currentChat?.service === CHATBOX_STRINGS.SKILLS_TECH) {
            getAssignedSubject();
        }
    }, [currentChat?.service]);

    const AREA_FILTERS = [
        { label: CHATBOX_STRINGS.AREA_ALL, value: 'all', width: '60px', color: theme.palette.secondary.main, background: theme.palette.primary.main },
        { label: CHATBOX_STRINGS.AREA_SAT_ACT, value: CHATBOX_STRINGS.SAT_ACT_COACH, width: '150px', color: theme.palette.primary.main, background: '#FFFCCF' },
        { label: CHATBOX_STRINGS.AREA_AI_TUTOR, value: CHATBOX_STRINGS.AI_TUTOR, width: '90px', color: '#C53636', background: '#F3D0D0' },
        { label: CHATBOX_STRINGS.AREA_AI_PROJECT_MENTOR, value: CHATBOX_STRINGS.AI_PROJECT_MENTOR, width: '160px', color: '#23A22A', background: '#D0FFD3' },
        { label: CHATBOX_STRINGS.AREA_AI_COUNSELLOR, value: CHATBOX_STRINGS.AI_COUNSELOR, width: '140px', color: '#126CAD', background: '#C3E5FD' },
        { label: CHATBOX_STRINGS.AREA_AI_SKILL_COACH, value: CHATBOX_STRINGS.SKILLS_TECH, width: '140px', color: '#A92271', background: '#F9CDE6' },
    ];

    const handleExportChat = () => {
        dispatch(setLoading({ loading: true }));
        if (!currentChat || !currentChat.messages || currentChat.messages.length === 0) {
            showNotification('No messages to export', 'warning');
            return;
        }

        let exportContent = `${currentChat.title}\n`;
        exportContent += `${'='.repeat(50)}\n\n`;
        exportContent += `Chat Type: ${ChatBotOption.find(opt => opt.value === currentChat?.service)?.label || 'AI Assistant'}\n`;
        exportContent += `Topic: ${currentChat.placeholder || 'General Discussion'}\n`;
        exportContent += `Date: ${moment().format('MMMM DD, YYYY HH:mm:ss')}\n`;
        exportContent += `${'='.repeat(50)}\n\n`;

        currentChat.messages.forEach((message, index) => {
            const sender = message.role === 'STUDENT' ? 'You' : 'AI Assistant';
            const timestamp = moment(message.created_at).format('MMMM DD, YYYY HH:mm:ss');
            exportContent += `[${index + 1}] ${sender} - ${timestamp}\n`;
            exportContent += `${'-'.repeat(40)}\n`;
            exportContent += `${message.message}\n\n`;
            if (message.attachment_urls && message.attachment_urls.length > 0) {
                exportContent += `Attachments:\n`;
                message.attachment_urls.forEach((file) => {
                    exportContent += `  - ${file.filename}\n`;
                });
                exportContent += `\n`;
            }
            exportContent += `${'='.repeat(50)}\n\n`;
        });

        exportContent += `\nSummary:\n`;
        exportContent += `${'-'.repeat(40)}\n`;
        exportContent += `Total Messages: ${currentChat.messages.length}\n`;
        exportContent += `Last Updated: ${moment(currentChat.updated_at).format('MMMM DD, YYYY HH:mm:ss')}\n`;

        const blob = new Blob([exportContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${currentChat.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_chat_export_${moment().format('YYYYMMDD_HHmmss')}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showNotification('Chat exported successfully', 'success');
        dispatch(setLoading({ loading: false }));
    };

    // Reusable copy icon button — used for both AI and student messages
    const CopyButton = ({ messageId, messageText }) => (
        <Tooltip title={copiedId === messageId ? 'Copied!' : 'Copy'} placement="top">
            <IconButton
                size="small"
                onClick={() => handleCopyMessage(messageText, messageId)}
                sx={{
                    p: '3px',
                    borderRadius: 1,
                    color: copiedId === messageId ? 'green' : theme.palette.background.dark,
                    '&:hover': { bgcolor: theme.palette.background.main },
                }}
            >
                {copiedId === messageId
                    ? <CheckIcon sx={{ fontSize: 15 }} />
                    : <ContentCopyIcon sx={{ fontSize: 15 }} />
                }
            </IconButton>
        </Tooltip>
    );

    // ── Attachment chip — reusable for both AI and Student messages ──
    const AttachmentChip = ({ file, isStudentMsg = false }) => (
        <Box
            onClick={() => handleOpenAttachment(file)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                border: isStudentMsg
                    ? '1px solid rgba(255,255,255,0.35)'
                    : '1px solid #e0e0e0',
                bgcolor: isStudentMsg
                    ? 'rgba(255,255,255,0.15)'
                    : '#f5f5f5',
                cursor: 'pointer',
                maxWidth: '260px',
                transition: 'all 0.15s ease',
                '&:hover': {
                    bgcolor: isStudentMsg
                        ? 'rgba(255,255,255,0.25)'
                        : '#ebebeb',
                    borderColor: isStudentMsg
                        ? 'rgba(255,255,255,0.55)'
                        : '#bbb',
                },
            }}
        >
            <DescriptionIcon
                sx={{
                    fontSize: 18,
                    color: isStudentMsg ? 'rgba(255,255,255,0.9)' : getFileIconColor(file.filename),
                    flexShrink: 0,
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    fontSize: 12,
                    color: isStudentMsg ? 'rgba(255,255,255,0.95)' : '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    fontWeight: 500,
                }}
            >
                {file.filename}
            </Typography>
            <DownloadIcon
                sx={{
                    fontSize: 14,
                    color: isStudentMsg ? 'rgba(255,255,255,0.7)' : '#888',
                    flexShrink: 0,
                }}
            />
        </Box>
    );

    // Blinking cursor shown while AI is streaming
    const cursorSx = {
        display: 'inline-block',
        width: '2px',
        height: '14px',
        bgcolor: '#333',
        ml: '2px',
        verticalAlign: 'middle',
        '@keyframes blink': {
            '0%,100%': { opacity: 1 },
            '50%': { opacity: 0 },
        },
        animation: 'blink 0.8s step-end infinite',
    };

    return (
        <Grid container spacing={2} sx={{ height: '100%', mt: 0.5 }}>
            {loading && <LoadingSpinner />}

            {/* Left Sidebar */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                {user?.user_type_id === UserTypeId.Student && (
                    <CommonButton
                        label={CHATBOX_STRINGS.NEW_CHAT_SESSION}
                        marginBottom="0px"
                        startIcon={<Add />}
                        handleClick={handleNewChat}
                    />
                )}
                <CommonCard
                    paddingBottom="0px"
                    borderRadius={1}
                    content={
                        <Fragment>
                            <TextField
                                fullWidth
                                placeholder={CHATBOX_STRINGS.SEARCH_PLACEHOLDER}
                                size="small"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#999' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { bgcolor: '#F8F8F8', borderRadius: 1, '& fieldset': { border: 'none' } },
                                }}
                            />
                            <Typography sx={{ fontWeight: 600, mb: 1.5, mt: 1 }}>
                                {CHATBOX_STRINGS.SELECT_AREAS}
                            </Typography>
                            <Divider />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {AREA_FILTERS.map((area) => (
                                    <CommonButton
                                        key={area.value}
                                        marginBottom="0px"
                                        label={area.label}
                                        color={selectedArea === area.value ? theme.palette.primary.main : area.color}
                                        border="1px solid #0000001A"
                                        background={selectedArea === area.value ? theme.palette.secondary.main : area.background}
                                        handleClick={() => setSelectedArea(area.value)}
                                        width={area.width}
                                        fontSize={13}
                                        fontWeight={600}
                                    />
                                ))}
                            </Box>
                        </Fragment>
                    }
                />
                <CommonCard
                    paddingBottom="0px"
                    borderRadius={1}
                    padding="0px"
                    content={
                        <Fragment>
                            <Typography sx={{ fontWeight: 600, p: 2 }}>
                                {CHATBOX_STRINGS.RECENT_HISTORY}
                            </Typography>
                            <Divider />
                            {chats.length > 0 ? (
                                <Box sx={{ height: '30vh', overflow: 'auto' }}>
                                    {chats.map((chat) => (
                                        <Fragment key={chat.chat_session_id}>
                                            <Box
                                                display="flex" gap={1} alignItems="center"
                                                sx={{ p: 1, cursor: 'pointer' }}
                                                onClick={() => handleSelectChat(chat.chat_session_id)}
                                            >
                                                <WatchLater sx={{ color: 'gray', fontSize: 20 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 600, color: '#333', mb: 0.3, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {chat.title}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#999', fontSize: '13px' }}>
                                                        {moment(chat.updated_at).fromNow()}
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleDeleteChat(chat.chat_session_id, e)}
                                                    sx={{ color: '#FF5252', '&:hover': { bgcolor: '#FFEBEE' }, ml: 1 }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                            <Divider />
                                        </Fragment>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <Typography color={theme.palette.background.dark}>
                                        {CHATBOX_STRINGS.CHAT_NOT_FOUND}
                                    </Typography>
                                </Box>
                            )}
                        </Fragment>
                    }
                />
            </Grid>

            {/* Main Content Area */}
            <Grid item md={6} xs={12}>
                <CommonCard
                    paddingBottom="0px"
                    borderRadius={1}
                    marginTop="0px"
                    height="72.5vh"
                    content={
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: currentChat?.title ? '100%' : '50%', mt: currentChat?.title ? 0 : 13 }}>

                            {/* Header */}
                            {currentChat?.title && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px' }}>
                                            {currentChat?.title}
                                        </Typography>
                                        <Typography sx={{ color: theme.palette.background.dark, fontSize: '12px' }}>
                                            {currentChat?.placeholder}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <CommonButton
                                            label={CHATBOX_STRINGS.EXPORT}
                                            marginBottom="0px"
                                            width="90px"
                                            height="30px"
                                            background={theme.palette.background.main}
                                            color={theme.palette.background.dark}
                                            borderRadius={3}
                                            endIcon={<ArrowRight sx={{ color: theme.palette.background.dark }} />}
                                            fontWeight={600}
                                            handleClick={() => handleExportChat()}
                                        />
                                        <MoreVert
                                            sx={{ color: theme.palette.background.dark, cursor: 'pointer' }}
                                            onClick={handleOpenMoreDialog}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {/* Messages Area */}
                            <Box sx={{ flex: 1, overflow: 'auto', px: showInitialOptions ? 0 : 2, display: 'flex', flexDirection: 'column' }}>
                                {showInitialOptions ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <Typography sx={{ fontWeight: 700, mb: 4, fontSize: 22, textAlign: 'center' }}>
                                            {CHATBOX_STRINGS.WELCOME_MESSAGE}
                                        </Typography>
                                        <Grid container spacing={2} sx={{ width: '85%' }}>
                                            {ChatBotOption.map((a, index) => (
                                                <Grid item xs={12} md={2.4} key={index} onClick={() => handleSelectOption(a.label)}>
                                                    <CommonCard
                                                        paddingBottom="0px"
                                                        borderRadius={2}
                                                        marginTop="0px"
                                                        height="100px"
                                                        background={a.background}
                                                        content={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', flexDirection: 'column', textAlign: 'center' }}>
                                                                <img src={a.image} alt={CHATBOX_STRINGS.ALT_ICON} />
                                                                <Typography mt={0.5} fontSize={14} color={a.color}>{a.label}</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ) : (
                                    <>
                                        {currentChat && currentChat.messages?.length > 0 ? (
                                            currentChat.messages.map((message) => {
                                                const isStreaming = message.id === streamingMessageId;
                                                const displayText = isStreaming ? streamingText : message.message;
                                                const isEditing = editingMessageId === message.id;
                                                const isStudent = message.role === 'STUDENT';

                                                return (
                                                    <Box
                                                        key={message.id}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: isStudent ? 'flex-end' : 'flex-start',
                                                            mb: 3,
                                                        }}
                                                    >
                                                        {/* ── AI message ── */}
                                                        {!isStudent && (
                                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>

                                                                    {/* Streaming indicator */}
                                                                    {isStreaming && displayText.length === 0 && (
                                                                        <Typography sx={{ fontSize: 12, color: theme.palette.background.dark, mb: 0.5 }}>
                                                                            DCP AI is typing...
                                                                        </Typography>
                                                                    )}

                                                                    {/* AI text — plain, no bubble (ChatGPT style) */}
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#1a1a1a',
                                                                            lineHeight: 1.75,
                                                                            whiteSpace: 'pre-wrap',
                                                                            fontSize: 14,
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        {displayText}
                                                                        {isStreaming && (
                                                                            <Box component="span" sx={cursorSx} />
                                                                        )}
                                                                    </Typography>

                                                                    {/* ── AI Attachments — shown after streaming completes ── */}
                                                                    {!isStreaming && message.attachment_urls?.length > 0 && (
                                                                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                                            {message.attachment_urls.map((file, idx) => (
                                                                                <AttachmentChip
                                                                                    key={idx}
                                                                                    file={file}
                                                                                    isStudentMsg={false}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}

                                                                    {/* Timestamp */}
                                                                    <Typography variant="caption" sx={{ color: '#999', fontSize: '10px', display: 'block', mt: 0.5 }}>
                                                                        {moment(message.created_at)?.format(CHATBOX_STRINGS.TIME_FORMAT)}
                                                                    </Typography>

                                                                    {/* Copy button for AI message — hidden while streaming */}
                                                                    {!isStreaming && (
                                                                        <Box sx={{ display: 'flex', mt: 0.5 }}>
                                                                            <CopyButton messageId={message.id} messageText={message.message} />
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* ── Student message ── */}
                                                        {isStudent && (
                                                            <>
                                                                {isEditing ? (
                                                                    /* Inline edit mode */
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '75%' }}>
                                                                        <TextField
                                                                            fullWidth
                                                                            multiline
                                                                            size="small"
                                                                            value={editingText}
                                                                            onChange={(e) => setEditingText(e.target.value)}
                                                                            autoFocus
                                                                            sx={{
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    borderRadius: 3,
                                                                                    fontSize: 14,
                                                                                },
                                                                            }}
                                                                        />
                                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                            <CommonButton
                                                                                label="Cancel"
                                                                                marginBottom="0px"
                                                                                height="30px"
                                                                                width="70px"
                                                                                background={theme.palette.background.main}
                                                                                color={theme.palette.background.dark}
                                                                                handleClick={handleCancelEdit}
                                                                            />
                                                                            <CommonButton
                                                                                label="Save & Send"
                                                                                marginBottom="0px"
                                                                                height="30px"
                                                                                width="120px"
                                                                                handleClick={() => {
                                                                                    handleSendMessage(message.message_id, editingText);
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                ) : (
                                                                    <>
                                                                        {/* ── Student Attachments — shown above the bubble ── */}
                                                                        {message.attachment_urls?.length > 0 && (
                                                                            <Box sx={{ mb: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                                                                                {message.attachment_urls.map((file, idx) => (
                                                                                    <AttachmentChip
                                                                                        key={idx}
                                                                                        file={file}
                                                                                        isStudentMsg={true}
                                                                                    />
                                                                                ))}
                                                                            </Box>
                                                                        )}

                                                                        {/* Student bubble — pill shape like ChatGPT */}
                                                                        <Paper
                                                                            elevation={0}
                                                                            sx={{
                                                                                bgcolor: theme.palette.secondary.main,
                                                                                px: 2,
                                                                                py: 1,
                                                                                borderRadius: '18px 18px 4px 18px',
                                                                                maxWidth: '75%',
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ color: 'white', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontSize: 14 }}
                                                                            >
                                                                                {message?.message}
                                                                            </Typography>
                                                                        </Paper>

                                                                        {/* Timestamp */}
                                                                        <Typography variant="caption" sx={{ color: '#999', fontSize: '10px', display: 'block', mt: 0.5 }}>
                                                                            {moment(message.created_at)?.format(CHATBOX_STRINGS.TIME_FORMAT)}
                                                                        </Typography>

                                                                        {/* Copy + Edit actions for student messages */}
                                                                        {user?.user_type_id === UserTypeId.Student && (
                                                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, justifyContent: 'flex-end' }}>
                                                                                <CopyButton messageId={message.id} messageText={message.message} />
                                                                                <Tooltip title="Edit message" placement="top">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => handleStartEdit(message)}
                                                                                        sx={{
                                                                                            p: '3px',
                                                                                            borderRadius: 1,
                                                                                            color: theme.palette.background.dark,
                                                                                            '&:hover': { bgcolor: theme.palette.background.main },
                                                                                        }}
                                                                                    >
                                                                                        <EditIcon sx={{ fontSize: 15 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Box>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </Box>
                                                );
                                            })
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.palette.background.dark }}>
                                                <Typography>{CHATBOX_STRINGS.START_CONVERSATION}</Typography>
                                            </Box>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </>
                                )}
                            </Box>

                            {/* Input Area */}
                            {user?.user_type_id === UserTypeId.Student && (
                                <Box sx={{ flexShrink: 0, pt: 1, pb: 1, bgcolor: 'white' }}>

                                    {/* Attached Files Preview */}
                                    {attachedFiles.length > 0 && (
                                        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1, px: 1, maxHeight: '80px', overflowY: 'auto' }}>
                                            {attachedFiles.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f0f0f0', border: '1px solid #ddd', px: 1, py: 0.5, borderRadius: 1, maxWidth: '200px' }}
                                                >
                                                    <DescriptionIcon sx={{ fontSize: 16, color: '#555', flexShrink: 0 }} />
                                                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: '12px' }}>
                                                        {file.name}
                                                    </Typography>
                                                    <IconButton size="small" onClick={() => handleRemoveFile(index)} sx={{ p: 0, flexShrink: 0 }}>
                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Option Selector (before first message) */}
                                    {selectedChatId && (!currentChat?.messages || currentChat.messages?.length < 1) && (
                                        <Box sx={{ mb: 1 }}>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={ChatBotOption.find(opt => opt.value === currentChat?.service)?.label || ''}
                                                    onChange={(e) => handleChangeOption(e.target.value)}
                                                    displayEmpty
                                                    sx={{ bgcolor: '#FFF8E1', '& fieldset': { border: 'none' }, fontWeight: 600, fontSize: '14px' }}
                                                >
                                                    {ChatBotOption.map((option) => (
                                                        <MenuItem key={option.label} value={option.label}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}

                                    {/* Subject Selector */}
                                    {selectedChatId && (currentChat?.service === CHATBOX_STRINGS.AI_TUTOR || currentChat?.service === CHATBOX_STRINGS.SKILLS_TECH) && (
                                        <Box sx={{ mb: 1 }}>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={currentChat?.subject_id || ''}
                                                    onChange={(e) => handleChangeSubject(e.target.value)}
                                                    displayEmpty
                                                    sx={{ bgcolor: '#FFF8E1', '& fieldset': { border: 'none' }, fontWeight: 600, fontSize: '14px' }}
                                                >
                                                    {subjects.map((option) => (
                                                        <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}

                                    {/* Text field + send row */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: currentChat?.messages?.length > 0 ? 'flex-start' : 'center',
                                            gap: 1,
                                            width: '100%',
                                            background: '#FAFAFA',
                                            flexDirection: currentChat?.messages?.length > 0 ? 'column' : 'row',
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            placeholder={getInputPlaceholder()}
                                            value={messageInput}
                                            onChange={(e) => setMessageInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            variant="outlined"
                                            size="small"
                                            disabled={!!streamingMessageId}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <IconButton onClick={() => fileInputRef.current?.click()} disabled={!!streamingMessageId}>
                                                            <img src="/Images/Attachment.png" alt={CHATBOX_STRINGS.ALT_ATTACHMENT} height="30px" />
                                                        </IconButton>
                                                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
                                                    </InputAdornment>
                                                ),
                                                sx: { height: 50, backgroundColor: '#f5f5f5' },
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    background: theme.palette.background.main,
                                                    fieldset: { border: 'none' },
                                                    '&:hover fieldset': { border: 'none' },
                                                    '&.Mui-focused fieldset': { border: 'none' },
                                                },
                                            }}
                                        />
                                        {currentChat?.messages?.length > 0 ? (
                                            <Box sx={{ m: 1, display: 'flex', justifyContent: 'space-between', width: '97%' }}>
                                                <FormControl size="small">
                                                    <Select
                                                        value={ChatBotOption.find(opt => opt.value === currentChat?.service)?.label || ''}
                                                        onChange={(e) => handleChangeOption(e.target.value)}
                                                        displayEmpty
                                                        IconComponent={KeyboardArrowDown}
                                                        renderValue={(selected) => {
                                                            const selectedOption = ChatBotOption.find(opt => opt.label === selected);
                                                            if (!selectedOption) return <em>{CHATBOX_STRINGS.SELECT_AN_OPTION}</em>;
                                                            return (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                    <Box sx={{ bgcolor: selectedOption.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <img src={selectedOption?.image} alt={CHATBOX_STRINGS.ALT_ICON} style={{ width: '20px', height: '20px' }} />
                                                                    </Box>
                                                                    <Typography sx={{ color: selectedOption.color, fontWeight: 600, fontSize: '15px' }}>
                                                                        {selectedOption.label}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        }}
                                                        sx={{ bgcolor: getSelectedOptionDetails()?.background || '#f5f5f5', borderRadius: 1, '& fieldset': { border: 'none' } }}
                                                    >
                                                        {ChatBotOption.map((option) => (
                                                            <MenuItem key={option.label} value={option.label}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                                                                        <img src={option?.image} alt={CHATBOX_STRINGS.ALT_ICON} style={{ width: '24px', height: '24px' }} />
                                                                    </Box>
                                                                    <Typography sx={{ color: option.color, fontWeight: 600, fontSize: '15px' }}>
                                                                        {option.label}
                                                                    </Typography>
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <CommonButton
                                                    label={CHATBOX_STRINGS.SEND}
                                                    marginBottom="0px"
                                                    fontWeight={600}
                                                    width="100px"
                                                    startIcon={<img src="/Images/Send.png" alt={CHATBOX_STRINGS.ALT_SEND} height="15px" />}
                                                    handleClick={() => handleSendMessage()}
                                                    height="35px"
                                                    disabled={!!streamingMessageId}
                                                />
                                            </Box>
                                        ) : (
                                            <CommonButton
                                                label={CHATBOX_STRINGS.SEND}
                                                marginBottom="0px"
                                                fontWeight={600}
                                                width="100px"
                                                startIcon={<img src="/Images/Send.png" alt={CHATBOX_STRINGS.ALT_SEND} height="15px" />}
                                                handleClick={() => handleSendMessage()}
                                                height="35px"
                                                disabled={!!streamingMessageId}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    }
                />
            </Grid>

            {/* Right Sidebar */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <CurrentPlan />
            </Grid>

            {/* Chat Details Dialog */}
            <Dialog open={moreDialogOpen} onClose={() => setMoreDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{CHATBOX_STRINGS.DIALOG_CHAT_DETAILS}</Typography>
                    <IconButton onClick={() => setMoreDialogOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {currentChat && (
                        <Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                    {CHATBOX_STRINGS.DIALOG_CHAT_TITLE}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isEditingTitle ? (
                                        <>
                                            <TextField value={editableTitle} onChange={(e) => setEditableTitle(e.target.value)} size="small" fullWidth autoFocus sx={{ flex: 1 }} />
                                            <IconButton onClick={handleUpdateTitle} color="primary" size="small"><SaveIcon /></IconButton>
                                            <IconButton onClick={() => { setIsEditingTitle(false); setEditableTitle(currentChat.title); }} size="small"><CloseIcon /></IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body1" sx={{ flex: 1 }}>{currentChat.title}</Typography>
                                            <IconButton onClick={() => setIsEditingTitle(true)} size="small"><EditIcon /></IconButton>
                                        </>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                    {CHATBOX_STRINGS.DIALOG_CHAT_TOPIC}
                                </Typography>
                                <Typography variant="body1">
                                    {currentChat.placeholder || CHATBOX_STRINGS.DIALOG_GENERAL_DISCUSSION}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                    {CHATBOX_STRINGS.DIALOG_CHAT_TYPE}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <img src={ChatBotOption.find(opt => opt.value === currentChat?.service)?.image} alt={CHATBOX_STRINGS.ALT_ICON} style={{ width: '24px', height: '24px' }} />
                                    <Typography>
                                        {ChatBotOption.find(opt => opt.value === currentChat?.service)?.label || CHATBOX_STRINGS.DIALOG_AI_ASSISTANT}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                    {CHATBOX_STRINGS.DIALOG_TOTAL_MESSAGES}
                                </Typography>
                                <Typography>
                                    {currentChat.messages?.length || 0} {CHATBOX_STRINGS.DIALOG_MESSAGES_SUFFIX}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                                    {CHATBOX_STRINGS.DIALOG_LAST_UPDATED}
                                </Typography>
                                <Typography>
                                    {moment(currentChat.updated_at).format(CHATBOX_STRINGS.DIALOG_DATE_FORMAT)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <CommonButton
                        label={CHATBOX_STRINGS.CLOSE}
                        handleClick={() => setMoreDialogOpen(false)}
                        marginBottom="0px"
                        background={theme.palette.background.main}
                        color={theme.palette.background.dark}
                    />
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default ChatBox;